import { useMemo } from 'react';
import { format } from 'date-fns';
import { enUS, ko } from 'date-fns/locale';

import { styled } from '@mui/material';
import { Grid, IconButtonGhost, Typography } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import IcChevronLeftSmallRegular from '@kep/kcds-icons/IcChevronLeftSmallRegular';
import IcChevronRightSmallRegular from '@kep/kcds-icons/IcChevronRightSmallRegular';

import { datePickerClasses } from '../theme';

import type { CustomHeaderProps } from '../types';

export default function DatePickerHeader({
  customHeaderCount,
  date,
  monthDate,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  locale = 'ko',
  monthsShown = 1,
}: CustomHeaderProps) {
  const { t } = useTranslation();
  const formatLocale = locale === 'ko' ? ko : enUS;

  const formattedDateString = useMemo(() => {
    const displayDate = monthDate || date;
    return format(displayDate, t('ui:datepicker.header.year_month_format'), {
      locale: formatLocale,
    });
  }, [date, monthDate, formatLocale, t]);

  // monthsShown이 2 이상일 때: 첫 번째 헤더는 왼쪽 chevron만, 마지막 헤더는 오른쪽 chevron만 표시
  const isMultipleMonths = monthsShown >= 2;
  const isFirstHeader = customHeaderCount === 0;
  const isLastHeader = customHeaderCount === monthsShown - 1;

  const showLeftChevron = !isMultipleMonths || isFirstHeader;
  const showRightChevron = !isMultipleMonths || isLastHeader;

  return (
    <DatePickerHeaderRoot container alignItems={'center'} sx={{ width: '100%' }}>
      <NavigationContainer>
        {showLeftChevron ? (
          <NavigationLeftButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
            <IcChevronLeftSmallRegular />
          </NavigationLeftButton>
        ) : (
          <PlaceholderButton />
        )}

        <CenteredTypography variant={'label/medium/lg'}>{formattedDateString}</CenteredTypography>

        {showRightChevron ? (
          <NavigationRightButton onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
            <IcChevronRightSmallRegular />
          </NavigationRightButton>
        ) : (
          <PlaceholderButton />
        )}
      </NavigationContainer>
    </DatePickerHeaderRoot>
  );
}

const DatePickerHeaderRoot = styled(Grid, {
  target: datePickerClasses.header,
})``;

const NavigationLeftButton = styled(IconButtonGhost, {
  target: datePickerClasses.prevMonthButton,
})`
  flex-shrink: 0;
`;

const NavigationRightButton = styled(IconButtonGhost, {
  target: datePickerClasses.nextMonthButton,
})`
  flex-shrink: 0;
`;

const PlaceholderButton = styled('div')`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

const CenteredTypography = styled(Typography, {
  target: datePickerClasses.monthYearDisplay,
})`
  flex: 1;
  text-align: center;
`;

const NavigationContainer = styled('div', {
  target: datePickerClasses.navigationContainer,
})`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;
